import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_PRODUCT,
  GET_MASTER_DATA_PRODUCTS,
  UPDATE_MASTER_DATA_PRODUCT,
} from "@src/domain/graphql/masterData";
import {
  MasterDataProduct,
  type IMasterDataProduct,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataProductDTO,
  GetMasterDataProductsDTO,
  UpdateMasterDataProductDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataProductRepository,
  MasterDataProductsListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapProductItem(item: IMasterDataProduct): MasterDataProduct {
  return MasterDataProduct.from({
    productId: item.productId,
    productKey: item.productKey,
    itemName: item.itemName,
    itemDescription: item.itemDescription ?? null,
    upcItemNumber: item.upcItemNumber ?? null,
    glItemNumber: item.glItemNumber ?? null,
    glItemDescription: item.glItemDescription ?? null,
    isActive: item.isActive,
    categoryId: item.categoryId ?? null,
    categoryName: item.categoryName ?? null,
    categoryKey: item.categoryKey ?? null,
    uomId: item.uomId ?? null,
    uomCode: item.uomCode ?? null,
    uomName: item.uomName ?? null,
    createdAt: item.createdAt ?? null,
    createdBy: item.createdBy ?? null,
    updatedAt: item.updatedAt ?? null,
    updatedBy: item.updatedBy ?? null,
  });
}

function toProductMutationParams(
  params: CreateMasterDataProductDTO | UpdateMasterDataProductDTO,
) {
  return {
    itemName: params.itemName,
    productKey: params.productKey,
    categoryId: params.categoryId,
    uomId: params.uomId,
    itemDescription: params.itemDescription,
    upcItemNumber: params.upcItemNumber,
    glItemNumber: params.glItemNumber ?? null,
    glItemDescription: params.glItemDescription ?? null,
    isActive: params.isActive,
  };
}

export class MasterDataProductRepository implements IMasterDataProductRepository {
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getProducts(
    params: GetMasterDataProductsDTO,
  ): Promise<MasterDataProductsListResult> {
    const { data, error } = await this.client.query<{
      masterDataProducts: {
        items: IMasterDataProduct[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_PRODUCTS,
      variables: {
        params: {
          search: params.search ?? null,
          includeInactive: params.includeInactive ?? true,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataProducts;
    if (!response) {
      throw new Error("Failed to fetch master data products");
    }

    return toMasterDataListResult(
      response,
      mapProductItem,
      params.page_number,
      params.page_size,
    );
  }

  async createProduct(
    params: CreateMasterDataProductDTO,
  ): Promise<MasterDataProduct> {
    return this.putProduct(
      CREATE_MASTER_DATA_PRODUCT,
      toProductMutationParams(params),
      (data) => data?.createMasterDataProduct,
      "Failed to create product",
    );
  }

  async updateProduct(
    params: UpdateMasterDataProductDTO,
  ): Promise<MasterDataProduct> {
    return this.putProduct(
      UPDATE_MASTER_DATA_PRODUCT,
      {
        productId: params.productId,
        ...toProductMutationParams(params),
      },
      (data) => data?.updateMasterDataProduct,
      "Failed to update product",
    );
  }

  private async putProduct(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataProduct?: IMasterDataProduct;
            updateMasterDataProduct?: IMasterDataProduct;
          }
        | null
        | undefined,
    ) => IMasterDataProduct | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataProduct> {
    const result = await this.client.mutate<{
      createMasterDataProduct?: IMasterDataProduct;
      updateMasterDataProduct?: IMasterDataProduct;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapProductItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
